import React, { useState, useEffect } from 'react';
import InputRange from 'react-input-range';
import { Link, useHistory, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import 'react-input-range/lib/css/index.css';

import BreadCrumb from '../../components/BreadCrumb';
import DefaultCreationForm, { DefaultCreationFormGroup } from '../../components/DefaultCreationForm';
import DefaultInput from '../../components/DefaultInput';
import { DefaultPageTitle } from '../../components/DefaultPageTitle';
import checkEmptyString from '../../helpers/check-empty-string';
import { getLevelById, updateLevel } from '../../services/level';

import { Container, StyledButton, CustomCreationFormGroup, Switch } from './styles';

interface EditLevelParams {
  levelId: string;
}

const EditLevel: React.FC = () => {
  const [name, setName] = useState('');
  const [minPoints, setMinPoints] = useState(0);
  const [maxPoints, setMaxPoints] = useState<number | null>(0);
  const [isLastLevel, setIsLastLevel] = useState(false);

  const history = useHistory();
  const { levelId } = useParams<EditLevelParams>();

  const handleUpdateLevel = async () => {
    try {
      if (checkEmptyString(name)) {
        throw new Error('Digite um nome para este nível');
      }

      if ((!isLastLevel && maxPoints === 0) || (!isLastLevel && Number(maxPoints) < minPoints)) {
        throw new Error('Dê um valor válido de pontos máximos');
      }

      await updateLevel(levelId, {
        name,
        range: {
          start: minPoints,
          end: maxPoints,
        },
      });

      Swal.fire({
        title: 'Sucesso!',
        text: 'Nível atualizado com sucesso',
        icon: 'success',
      });

      history.goBack();
    } catch (error: any) {
      Swal.fire({
        title: 'Erro!',
        text: `Houve um problema ao atualizar este nível! ${
          error?.response?.data?.message || error.message || 'Ocorreu um erro inesperado'
        }`,
        icon: 'error',
      });
    }
  };

  useEffect(() => {
    const fetchLevel = async () => {
      try {
        const level = await getLevelById(levelId);

        setName(level.name);
        setMinPoints(level?.range?.start || 0);
        setMaxPoints(level.range.end ?? 0);
        setIsLastLevel(level.range.end === null);
      } catch (error: any) {
        Swal.fire({
          title: 'Erro!',
          text: `Houve um problema ao recuperar os dados deste nível! ${
            error?.response?.data?.message || error.message || 'Ocorreu um erro inesperado'
          }`,
          icon: 'error',
        });
      }
    };

    fetchLevel();
  }, [levelId]);

  return (
    <Container>
      <BreadCrumb
        crumbs={[
          <Link to="/dashboard">Dashboard</Link>,
          <Link to="/levels">Gameficação</Link>,
          <span>Editar nível</span>,
        ]}
      />

      <DefaultPageTitle>Editar nível</DefaultPageTitle>

      <DefaultCreationForm>
        <DefaultCreationFormGroup>
          <label>Último nível</label>
          <Switch>
            <input type="checkbox" checked={isLastLevel} onChange={(e) => setIsLastLevel(e.target.checked)} />
            <span className="slider round"></span>
          </Switch>
        </DefaultCreationFormGroup>

        <DefaultCreationFormGroup>
          <label className="required" htmlFor="name">
            Nome
          </label>
          <DefaultInput
            id="name"
            placeholder="Digite o nome deste nível"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={25}
          />
        </DefaultCreationFormGroup>

        <CustomCreationFormGroup>
          <label className="required" htmlFor="minPoints">
            Pontuação mínima
          </label>
          <InputRange
            maxValue={100}
            minValue={0}
            value={minPoints}
            onChange={(value) => setMinPoints(value as number)}
          />

          <span>Valor selecionado: {minPoints}</span>
        </CustomCreationFormGroup>

        {isLastLevel ? null : (
          <CustomCreationFormGroup>
            <label className="required" htmlFor="minPoints">
              Pontuação máxima
            </label>

            <InputRange
              maxValue={101}
              minValue={minPoints + 1}
              value={maxPoints as number}
              onChange={(value) => setMaxPoints(value as number)}
            />

            <span>Valor selecionado: {maxPoints}</span>
          </CustomCreationFormGroup>
        )}

        <CustomCreationFormGroup>
          <StyledButton type="button" onClick={handleUpdateLevel} style={{ marginTop: 100 }}>
            SALVAR
          </StyledButton>
        </CustomCreationFormGroup>
      </DefaultCreationForm>
    </Container>
  );
};

export default EditLevel;
